import React, { useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { CheckCircle2, X, Heart, Flame, Crown, ArrowLeft } from 'lucide-react';
import { PaywallConfig } from '@/types/paywall';
import { PaywallPurchaseButton } from '@/components/PaywallPurchaseButton';

interface PaywallModalProps {
  isOpen: boolean;
  onClose: () => void;
  onBack?: () => void;
  config: PaywallConfig;
}

// Map plan names to colors and icons
const getPlanTheme = (planName: string) => {
  const name = planName.toLowerCase();
  
  if (name.includes('flerte')) {
    return {
      gradient: 'bg-gradient-to-br from-purple-400 via-purple-500 to-purple-600',
      icon: Heart,
      textColor: 'text-white'
    };
  } else if (name.includes('desejo')) {
    return {
      gradient: 'bg-gradient-to-br from-pink-400 via-pink-500 to-pink-600',
      icon: Flame,
      textColor: 'text-white'
    };
  } else if (name.includes('atracao') || name.includes('atração')) {
    return {
      gradient: 'bg-gradient-to-br from-blue-400 via-purple-400 to-purple-500',
      icon: Crown,
      textColor: 'text-white'
    };
  } else if (name.includes('paixao') || name.includes('paixão')) {
    return {
      gradient: 'bg-gradient-to-br from-red-400 via-red-500 to-red-600',
      icon: Crown,
      textColor: 'text-white'
    };
  }
  
  // Default theme
  return {
    gradient: 'bg-gradient-to-br from-primary/80 to-primary',
    icon: Heart,
    textColor: 'text-white'
  };
};

export const PaywallModal: React.FC<PaywallModalProps> = ({
  isOpen,
  onClose,
  onBack,
  config
}) => {
  const [period, setPeriod] = useState<'mensal' | 'anual'>('mensal');

  const handleBackAction = () => {
    onClose(); // Fecha a modal primeiro
    if (onBack) {
      setTimeout(() => onBack(), 100); // Navega depois de fechar
    }
  };
  
  if (!config) return null;
  
  const currentPlans = config.requiredPlans[period] || [];

  return (
    <Sheet open={isOpen} onOpenChange={handleBackAction}>
      <SheetContent 
        side="bottom" 
        className="h-[90vh] p-0 flex flex-col bg-background"
      >
        {/* Header */}
        <SheetHeader className="p-6 pb-4 text-center border-b border-border">
          
          <SheetTitle className="text-2xl font-bold text-foreground mb-2">
            {config.title}
          </SheetTitle>
          
          <SheetDescription className="text-muted-foreground text-base leading-relaxed max-w-md mx-auto">
            {config.description}
          </SheetDescription>
        </SheetHeader>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {config.showPlans && (
            <div className="space-y-6 max-w-sm mx-auto">
              {/* Period Toggle */}
              <div className="flex justify-center">
                <ToggleGroup
                  type="single"
                  value={period}
                  onValueChange={(value) => value && setPeriod(value as 'mensal' | 'anual')}
                  className="bg-muted rounded-lg p-1 grid grid-cols-2 w-full max-w-[200px]"
                >
                  <ToggleGroupItem
                    value="mensal"
                    className="data-[state=on]:bg-background data-[state=on]:text-foreground data-[state=on]:shadow-sm text-sm font-medium rounded-md px-3 py-2"
                  >
                    Mensal
                  </ToggleGroupItem>
                  <ToggleGroupItem
                    value="anual"
                    className="data-[state=on]:bg-background data-[state=on]:text-foreground data-[state=on]:shadow-sm text-sm font-medium rounded-md px-3 py-2"
                  >
                    Anual
                  </ToggleGroupItem>
                </ToggleGroup>
              </div>

              {/* Plans */}
              <div className="space-y-4">
                {currentPlans.map((plan) => {
                const theme = getPlanTheme(plan.name);
                const IconComponent = theme.icon;
                
                return (
                  <div 
                    key={plan.id}
                    className={`relative rounded-2xl p-6 ${theme.gradient} text-white shadow-lg`}
                  >
                    {/* Plan Icon */}
                    <div className="flex items-center justify-center w-12 h-12 bg-white/20 rounded-full mb-4 mx-auto">
                      <IconComponent className="h-6 w-6 text-white" />
                    </div>
                    
                    {/* Plan Name */}
                    <h3 className="text-xl font-bold text-center mb-2 text-white">
                      {plan.name}
                    </h3>
                    
                    {/* Price */}
                    <div className="text-center mb-6">
                      <div className="flex items-baseline justify-center gap-1">
                        <span className="text-3xl font-bold text-white">
                          R$ {plan.price.toFixed(2).replace('.', ',')}
                        </span>
                        <span className="text-lg text-white/80">
                          /{plan.interval}
                        </span>
                      </div>
                    </div>
                    
                    {/* Features */}
                    <div className="space-y-3 mb-6">
                      {plan.features.map((feature, index) => (
                        <div key={index} className="flex items-center gap-3">
                          <CheckCircle2 className="h-5 w-5 text-white flex-shrink-0" />
                          <span className="text-white text-sm leading-relaxed">{feature}</span>
                        </div>
                      ))}
                    </div>

                    {/* Purchase Button */}
                    <PaywallPurchaseButton
                      planId={plan.id}
                      planName={plan.name}
                      planPrice={plan.price}
                      period={period}
                      text={config.upgradeText}
                      className="w-full bg-white/20 hover:bg-white/30 text-white border border-white/30 backdrop-blur-sm font-semibold py-3 rounded-xl transition-all duration-200"
                    />
                  </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 pt-4 border-t border-border">
          <Button
            variant="ghost"
            onClick={handleBackAction}
            className="w-full h-12 text-muted-foreground hover:text-foreground hover:bg-muted rounded-xl"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};